import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeFr from '@angular/common/locales/fr';
import { ApplicationConfig, LOCALE_ID, importProvidersFrom, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withHashLocation } from '@angular/router';
import { createStandaloneTranslateLoader } from '@kiraah-shared/app-skeleton';
import { LvfAppModule, LvfMissionType, LvfOperationType, LvfTrainingSessionPackageType } from '@kiraah-shared/common';
import {
  AuthInterceptor,
  BASE_COMPONENT_CONFIG,
  AGENCY_DETAIL_COMPONENT_LOADER,
  BUSINESS_UNIT_DETAIL_COMPONENT_LOADER,
  ComponentLoader,
  createMemoizedComponentLoader,
  createMemoizedMutationInvalidator,
  ESIGNATURE_WIZARD_COMPONENT_LOADER,
  IDENTITY_ACCESSIBILITY_SETTINGS_COMPONENT_LOADER,
  COMPENSATION_MEASURE_DETAIL_COMPONENT_LOADER,
  IDENTITY_DETAIL_COMPONENT_LOADER,
  IDENTITY_TAB_COMPONENT_LOADER,
  IDENTITY_PASSWORD_CHANGE_COMPONENT_LOADER,
  IDENTITY_PICKER_COMPONENT_LOADER,
  STUDENT_DETAIL_COMPONENT_LOADER,
  MENTOR_PICKER_COMPONENT_LOADER,
  MISSION_ACTIONS_COMPONENT_REGISTRY,
  LEARNING_CAMPAIGN_EDITOR_COMPONENT_LOADER,
  QUICK_ACCESS_COMPONENT_LOADER,
  SEQUENCE_LIST_COMPONENT_LOADER,
  STUDENT_PICKER_COMPONENT_LOADER,
  THIRDPART_ENTERPRISE_LIST_COMPONENT_LOADER,
  THIRDPART_ENTERPRISE_TAB_COMPONENT_LOADER,
  PEDAGOGICAL_WORKFLOW_TAB_COMPONENT_LOADER,
  THIRDPART_ACTIVITY_DOMAIN_DETAIL_COMPONENT_LOADER,
  TICKET_WIZARD_COMPONENT_LOADER,
  TRAINING_SESSION_ACTIONS_COMPONENT_REGISTRY,
  TRAINING_SESSION_PICKER_COMPONENT_LOADER,
  TRAINING_SESSION_CARD_COMPONENT_LOADER,
  TRAINING_SESSION_WIZARD_MUTATION_INVALIDATOR,
} from '@kiraah-shared/core';
import { ENVIRONMENT } from '@kiraah-shared/environment';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { provideHighlightOptions } from 'ngx-highlightjs';
import { provideToastr } from 'ngx-toastr';

import { CustomPaginatorIntl } from '@kiraah-shared/core';
import { environment } from '../environments/environment';
import { appInitializer } from './app-initializer';
import { routes } from './app.routes';

function translateLoaderFactory(http: HttpClient): TranslateLoader {
  return (createStandaloneTranslateLoader('default') as (http: HttpClient) => TranslateLoader)(http);
}

registerLocaleData(localeFr, 'fr');

const loadIdentityDetailComponent: ComponentLoader = createMemoizedComponentLoader(() =>
  import('@kiraah-shared/daily-activity/pickers').then(m => m.IdentityDetailComponent)
);

const loadIdentityTabComponent: ComponentLoader = createMemoizedComponentLoader(() =>
  import('@kiraah-shared/identity').then(m => m.IdentityTabComponent)
);

const loadIdentityPickerComponent: ComponentLoader = createMemoizedComponentLoader(() =>
  import('@kiraah-shared/daily-activity/pickers').then(m => m.IdentityPickerComponent)
);

const loadIdentityAccessibilitySettingsComponent: ComponentLoader = createMemoizedComponentLoader(() =>
  import('@kiraah-shared/identity').then(m => m.IdentityAccessibilitySettingComponent)
);

const loadIdentityPasswordChangeComponent: ComponentLoader = createMemoizedComponentLoader(() =>
  import('@kiraah-shared/identity').then(m => m.IdentityPasswordChangeComponent)
);

const loadStudentDetailComponent: ComponentLoader = createMemoizedComponentLoader(() =>
  import('@kiraah-shared/daily-activity/pickers').then(m => m.StudentDetailComponent)
);

const loadStudentPickerComponent: ComponentLoader = createMemoizedComponentLoader(() =>
  import('@kiraah-shared/daily-activity/pickers').then(m => m.StudentPickerComponent)
);

const loadMentorPickerComponent: ComponentLoader = createMemoizedComponentLoader(() =>
  import('@kiraah-shared/daily-activity/pickers').then(m => m.MentorPickerComponent)
);

const loadTrainingSessionPickerComponent: ComponentLoader = createMemoizedComponentLoader(() =>
  import('@kiraah-shared/daily-activity').then(m => m.TrainingSessionPickerComponent)
);

const loadTrainingSessionCardComponent: ComponentLoader = createMemoizedComponentLoader(() =>
  import('@kiraah-shared/academy').then(m => m.TrainingSessionCardComponent)
);

const loadSequenceListComponent: ComponentLoader = createMemoizedComponentLoader(() =>
  import('@kiraah-shared/academy').then(m => m.SequenceListComponent)
);

const trainingSessionWizardMutationInvalidator = createMemoizedMutationInvalidator(() =>
  import('@kiraah-shared/academy/training-session-wizard').then((module) => (
    coordinator,
    uid,
    mutationKind,
    stepKey
  ) =>
    module.applyMutationInvalidation(
      coordinator as Parameters<typeof module.applyMutationInvalidation>[0],
      uid,
      mutationKind as Parameters<typeof module.applyMutationInvalidation>[2],
      stepKey as Parameters<typeof module.applyMutationInvalidation>[3]
    )
  )
);

const loadCompensationMeasureDetailComponent: ComponentLoader = createMemoizedComponentLoader(() =>
  import('@kiraah-shared/academy').then(m => m.CompensationMeasureDetailComponent)
);

const loadQuickAccessComponent: ComponentLoader = createMemoizedComponentLoader(() =>
  import('@kiraah-shared/daily-activity/quick-access').then(m => m.QuickAccessComponent)
);

const loadMissionAuditActions: ComponentLoader = createMemoizedComponentLoader(() =>
  import('@kiraah-shared/consulting/mission-actions').then(m => m.MissionAuditActionsComponent)
);

const loadMissionConsultingActions: ComponentLoader = createMemoizedComponentLoader(() =>
  import('@kiraah-shared/consulting/mission-actions').then(m => m.MissionConsultingActionsComponent)
);

const loadMissionBtpActions: ComponentLoader = createMemoizedComponentLoader(() =>
  import('@kiraah-shared/consulting/mission-actions').then(m => m.MissionBtpActionsComponent)
);

const loadTrainingSessionStandardActions: ComponentLoader = createMemoizedComponentLoader(() =>
  import('@kiraah-shared/academy/training-session-actions').then(m => m.TrainingSessionStandardActionsComponent)
);

const loadTrainingSessionSurveySigningActions: ComponentLoader = createMemoizedComponentLoader(() =>
  import('@kiraah-shared/academy/training-session-actions').then(m => m.TrainingSessionSurveySigningActionsComponent)
);

const loadLearningCampaignEditorComponent: ComponentLoader = createMemoizedComponentLoader(() =>
  import('@kiraah-shared/daily-activity/learning-campaign').then(m => m.LearningCampaignEditorComponent)
);

const loadESignatureWizardComponent: ComponentLoader = createMemoizedComponentLoader(() =>
  import('@kiraah-shared/daily-activity/signature').then(m => m.ESignatureWizardComponent)
);

const loadTicketWizardComponent: ComponentLoader = createMemoizedComponentLoader(() =>
  import('@kiraah-shared/sales').then(m => m.TicketWizardComponent)
);

const loadThirdPartEnterpriseTabComponent: ComponentLoader = createMemoizedComponentLoader(() =>
  import('@kiraah-shared/thirdpart').then(m => m.ThirdPartEnterpriseTabComponent)
);

const loadPedagogicalWorkflowTabComponent: ComponentLoader = createMemoizedComponentLoader(() =>
  import('@kiraah-shared/academy').then(m => m.PedagogicalWorkflowDetailComponent)
);

const loadThirdPartEnterpriseListComponent: ComponentLoader = createMemoizedComponentLoader(() =>
  import('@kiraah-shared/thirdpart').then(m => m.ThirdPartEnterpriseListComponent)
);

const loadBusinessUnitDetailComponent: ComponentLoader = createMemoizedComponentLoader(() =>
  import('@kiraah-shared/organization').then(m => m.BusinessUnitDetailComponent)
);

const loadAgencyDetailComponent: ComponentLoader = createMemoizedComponentLoader(() =>
  import('@kiraah-shared/organization').then(m => m.AgencyDetailComponent)
);

const loadThirdPartActivityDomainDetailComponent: ComponentLoader = createMemoizedComponentLoader(() =>
  import('@kiraah-shared/thirdpart').then(m => m.ThirdPartActivityDomainDetailComponent)
);

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(routes, withHashLocation()),
    provideHighlightOptions({
      fullLibraryLoader: () => import('highlight.js')
    }),
    provideToastr({
      timeOut: 5000,
      positionClass: 'toast-top-right',
      preventDuplicates: true
    }),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: translateLoaderFactory,
          deps: [HttpClient]
        }
      })
    ),
    { provide: MatPaginatorIntl, useClass: CustomPaginatorIntl },
    { provide: LOCALE_ID, useValue: 'fr' },
    { provide: ENVIRONMENT, useValue: environment },
    {
      provide: BASE_COMPONENT_CONFIG,
      useValue: {
        lvfAppModule: LvfAppModule.NONE,
        lvfOperationType: LvfOperationType.UNKNOWN
      }
    },
    { provide: IDENTITY_DETAIL_COMPONENT_LOADER, useValue: loadIdentityDetailComponent },
    { provide: IDENTITY_TAB_COMPONENT_LOADER, useValue: loadIdentityTabComponent },
    { provide: IDENTITY_PICKER_COMPONENT_LOADER, useValue: loadIdentityPickerComponent },
    { provide: IDENTITY_ACCESSIBILITY_SETTINGS_COMPONENT_LOADER, useValue: loadIdentityAccessibilitySettingsComponent },
    { provide: IDENTITY_PASSWORD_CHANGE_COMPONENT_LOADER, useValue: loadIdentityPasswordChangeComponent },
    { provide: STUDENT_DETAIL_COMPONENT_LOADER, useValue: loadStudentDetailComponent },
    { provide: STUDENT_PICKER_COMPONENT_LOADER, useValue: loadStudentPickerComponent },
    { provide: MENTOR_PICKER_COMPONENT_LOADER, useValue: loadMentorPickerComponent },
    { provide: TRAINING_SESSION_PICKER_COMPONENT_LOADER, useValue: loadTrainingSessionPickerComponent },
    { provide: TRAINING_SESSION_CARD_COMPONENT_LOADER, useValue: loadTrainingSessionCardComponent },
    { provide: SEQUENCE_LIST_COMPONENT_LOADER, useValue: loadSequenceListComponent },
    { provide: TRAINING_SESSION_WIZARD_MUTATION_INVALIDATOR, useValue: trainingSessionWizardMutationInvalidator },
    { provide: COMPENSATION_MEASURE_DETAIL_COMPONENT_LOADER, useValue: loadCompensationMeasureDetailComponent },
    { provide: ESIGNATURE_WIZARD_COMPONENT_LOADER, useValue: loadESignatureWizardComponent },
    { provide: TICKET_WIZARD_COMPONENT_LOADER, useValue: loadTicketWizardComponent },
    { provide: QUICK_ACCESS_COMPONENT_LOADER, useValue: loadQuickAccessComponent },
    { provide: LEARNING_CAMPAIGN_EDITOR_COMPONENT_LOADER, useValue: loadLearningCampaignEditorComponent },
    { provide: THIRDPART_ENTERPRISE_TAB_COMPONENT_LOADER, useValue: loadThirdPartEnterpriseTabComponent },
    { provide: PEDAGOGICAL_WORKFLOW_TAB_COMPONENT_LOADER, useValue: loadPedagogicalWorkflowTabComponent },
    { provide: THIRDPART_ENTERPRISE_LIST_COMPONENT_LOADER, useValue: loadThirdPartEnterpriseListComponent },
    { provide: BUSINESS_UNIT_DETAIL_COMPONENT_LOADER, useValue: loadBusinessUnitDetailComponent },
    { provide: AGENCY_DETAIL_COMPONENT_LOADER, useValue: loadAgencyDetailComponent },
    { provide: THIRDPART_ACTIVITY_DOMAIN_DETAIL_COMPONENT_LOADER, useValue: loadThirdPartActivityDomainDetailComponent },
    {
      provide: MISSION_ACTIONS_COMPONENT_REGISTRY,
      useValue: {
        [LvfMissionType.AUDIT]: loadMissionAuditActions,
        [LvfMissionType.CONSULTING]: loadMissionConsultingActions,
        [LvfMissionType.BTP]: loadMissionBtpActions
      }
    },
    {
      provide: TRAINING_SESSION_ACTIONS_COMPONENT_REGISTRY,
      useValue: {
        [LvfTrainingSessionPackageType.STANDARD]: loadTrainingSessionStandardActions,
        [LvfTrainingSessionPackageType.SURVEY_SIGNING]: loadTrainingSessionSurveySigningActions
      }
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    provideAppInitializer(appInitializer)
  ]
};
